import imageCompression from "browser-image-compression"
import React, { useEffect, useState } from "react"
import type ReactQuill from "react-quill"
import FormatOverlay from "./format-overlay.tsx"
import { ApiariesIcon } from "@/assets/icons/bees/apiaries-icon.tsx"
import HiveIcon from "@/assets/icons/bees/hive-icon.tsx"
import NrBeesBeginningIcon from "@/assets/icons/bees/nr-bees-beginning-icon.tsx"
import FormatTextIcon from "@/assets/icons/misc/format-text-icon.tsx"
import ImageIcon from "@/assets/icons/misc/image-icon.tsx"
import BirdIcon from "@/assets/svg/bird-icon.svg?react"
import CropIcon from "@/assets/svg/crop-planting-icon.svg?react"
import FieldIcon from "@/assets/svg/parcel-icon.svg?react"
import RenderFunctionDrawer from "@/components/render-function-drawer/render-function-drawer.tsx"
import { useGetApiariesQuery } from "@/features/bees/api/apiaries-api.ts"
import { useGetAllHivesQuery } from "@/features/bees/api/hives-api.ts"
import { useGetAllQueensQuery } from "@/features/bees/api/queens-api.ts"
import { ApiaryCard } from "@/features/bees/components/apiaries/associated-apiaries-list/associated-apiaries-list.tsx"
import { HiveCard } from "@/features/bees/components/apiaries/associated-hives-list/associated-hives-list.tsx"
import { AssociatedQueenCard } from "@/features/bees/components/apiaries/associated-queens-list/associated-queens-list.tsx"
import { useGetBirdsGroupsQuery } from "@/features/birds-groups/api/birds-groups-api.ts"
import { AssociatedBirdGroupCard } from "@/features/birds-groups/components/associated-birds-groups-list/associated-birds-groups-list.tsx"
import { useGetAllCropPlantingsQuery } from "@/features/crops/api/crops-planting-api.ts"
import { AssociatedCropPlantingCard } from "@/features/crops/components/associated-crop-plantings-list/associated-crop-plantings-list.tsx"
import { useGetAnimalsQuery } from "@/features/farm/api/animals-api.ts"
import DrawerAnimals from "@/features/farm/components/drawer-animals/drawer-animals.tsx"
import useFarmHasMammals from "@/features/farm/hooks/use-farm-has-mammals.ts"
import { type NoteDraft } from "@/features/notes"
import AssocAnimalFromNoteTrigger from "@/features/notes/components/assoc-entity-from-note-trigger/assoc-animal-from-note-trigger.tsx"
import AssocTaskFromNoteTrigger from "@/features/notes/components/assoc-entity-from-note-trigger/assoc-task-from-note-trigger.tsx"
import { useGetParcelsQuery } from "@/features/parcels/api/parcels-api.ts"
import { AssociatedParcelCard } from "@/features/parcels/components/associated-parcels-list/associated-parcels-list.tsx"
import useFarmPermissions from "@/features/permissions/hooks/use-farm-permissions.ts"
import DrawerTasks from "@/features/tasks/components/drawer-tasks/drawer-tasks.tsx"
import { cn } from "@/utils/styles.ts"

/**
 * Props for the CustomToolbar component.
 */
interface CustomToolbarProps {
  /**
   * Reference to the ReactQuill editor instance.
   */
  editorRef: React.RefObject<ReactQuill>
  draftNote: NoteDraft
  onAnimalsChange: (animalIDs: string[]) => void
  onHivesChange: (hiveIDs: string[]) => void
  onApiariesChange: (apiaryIDs: string[]) => void
  onQueensChange: (queenIDs: string[]) => void
  onTasksChange: () => void
  onBirdsGroupsChange: (birdGroupIDs: string[]) => void
  onParcelsChange: (parcelIDs: string[]) => void
  onCropPlantingsChange: (cropPlantingIDs: string[]) => void
  onClick?: () => void
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  editorRef,
  draftNote,
  onAnimalsChange,
  onHivesChange,
  onApiariesChange,
  onTasksChange,
  onQueensChange,
  onBirdsGroupsChange,
  onParcelsChange,
  onCropPlantingsChange,
  onClick,
}) => {
  const isMammal = useFarmHasMammals()
  const { data: animals } = useGetAnimalsQuery()
  const { permissions } = useFarmPermissions()
  const [isFormatActive, setIsFormatActive] = useState(false)
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    header: "normal",
    list: "",
  })

  const { data: apiaries } = useGetApiariesQuery()
  const apiariesOptions =
    (apiaries &&
      apiaries.map((apiary) => ({
        value: apiary.id,
        label: apiary.name,
      }))) ||
    []

  const { data: hives } = useGetAllHivesQuery()
  const hiveOptions =
    hives?.map((hive) => ({
      label: hive.code,
      value: hive.id,
    })) || []

  const { data: queens } = useGetAllQueensQuery()
  const queenOptions =
    queens?.map((queen) => ({
      label: queen.name,
      value: queen.id,
    })) || []

  const { data: birdsGroups } = useGetBirdsGroupsQuery()
  const birdsGroupsOptions =
    birdsGroups?.map((birdGroup) => ({
      label: birdGroup.name,
      value: birdGroup.id,
    })) || []

  const { data: parcels = [] } = useGetParcelsQuery()
  const parcelOptions =
    parcels?.map((parcel) => ({
      label: parcel.name,
      value: parcel.id,
    })) || []

  const { data: cropPlantings = [] } = useGetAllCropPlantingsQuery({})
  const cropPlantingsOptions =
    cropPlantings?.map((cropPlanting) => ({
      label: cropPlanting.crop_name,
      value: cropPlanting.id,
    })) || []

  useEffect(() => {
    const editor = editorRef.current?.getEditor()
    const updateFormatState = () => {
      if (!editor) return

      const formats = editor.getFormat()
      setFormatState({
        bold: !!formats.bold,
        italic: !!formats.italic,
        underline: !!formats.underline,
        strike: !!formats.strike,
        header: formats.header ? `h${formats.header}` : "normal",
        list: formats.list || "",
      })
    }

    editor?.on("selection-change", updateFormatState)

    return () => {
      editor?.off("selection-change", updateFormatState)
    }
  }, [editorRef])

  /**
   * Toggles the specified format in the editor.
   * @param {keyof typeof formatState} format - The format to toggle-switch.
   * @param {boolean | string | number} [value] - The value to set for the format (optional).
   */
  const toggleFormat = (
    format: keyof typeof formatState,
    value?: boolean | string | number,
  ) => {
    const editor = editorRef.current?.getEditor()
    if (!editor) return

    switch (format) {
      case "bold":
      case "italic":
      case "underline":
      case "strike":
        editor.format(
          format,
          value !== undefined ? value : !formatState[format],
        )
        break
      case "header":
        editor.format(format, formatState.header === value ? false : value)
        setIsFormatActive(false)
        break
      case "list":
        editor.format("list", formatState.list === value ? false : value)
        setIsFormatActive(false)
        break
      default:
        console.warn("Unsupported format:", format)
    }

    setTimeout(updateFormatState, 0)
  }

  /**
   * Updates the format state based on the current selection in the editor.
   */
  const updateFormatState = () => {
    const editor = editorRef.current?.getEditor()
    if (!editor) return

    const formats = editor.getFormat()
    setFormatState((prevState) => ({
      ...prevState,
      bold: !!formats.bold,
      italic: !!formats.italic,
      underline: !!formats.underline,
      strike: !!formats.strike,
      header: formats.header || 0,
      list: formats.list || "",
    }))
  }

  /**
   * Adjusts the indentation of the selected text in the editor.
   * @param {"indent" | "outdent"} direction - The direction of indentation adjustment.
   */
  const adjustIndentation = (direction: "indent" | "outdent") => {
    const editor = editorRef.current?.getEditor()
    if (!editor) return

    const range = editor.getSelection()
    if (!range) return

    const currentFormat = editor.getFormat(range)
    const currentIndent = currentFormat.indent || 0
    if (direction === "indent") {
      editor.format("indent", currentIndent + 1) // Increment indentation
    } else if (direction === "outdent" && currentIndent > 0) {
      editor.format("indent", currentIndent - 1) // Decrement indentation
    }
  }

  /**
   * Inserts an image into the editor.
   */
  const insertImage = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return

      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      }

      try {
        const compressedFile = await imageCompression(file, options)
        const reader = new FileReader()
        reader.onload = (e) => {
          const editor = editorRef.current?.getEditor()
          if (!editor) return

          const range = editor.getSelection(true)
          const newIndex = range.index + 1

          editor.insertText(newIndex, "\n")
          editor.insertEmbed(range.index, "image", e.target?.result, "user")
          editor.setSelection(newIndex, 0)
          editor.insertText(newIndex, "\n")
        }
        reader.readAsDataURL(compressedFile)
      } catch (error) {
        console.log(error)
      }
    }
    input.click()
  }

  return (
    <span onClick={onClick}>
      <div className="gap-1.25 mt-2.5 box-border flex w-full flex-wrap items-center justify-center px-8">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setIsFormatActive(true)
          }}
          className={cn(
            "flex w-fit items-center justify-center rounded-lg border-none bg-transparent p-2.5",
            isFormatActive && "bg-yellow-500",
          )}
        >
          <FormatTextIcon is_active={isFormatActive} />
        </button>

        <div
          className={"flex h-[45px] w-[60px] items-center justify-center"}
          onClick={insertImage}
        >
          <ImageIcon alt="Insert Image" />
        </div>

        <div className={"flex h-[45px] w-[60px] items-center justify-center"}>
          <DrawerTasks
            currentValue={draftNote.tasks}
            TriggerComponent={AssocTaskFromNoteTrigger}
            onSaveClick={onTasksChange}
          />
        </div>

        {isMammal && animals && animals.length > 0 && (
          <div className={"flex h-[45px] w-[60px] items-center justify-center"}>
            <DrawerAnimals
              currentValue={draftNote.animals}
              onSaveClick={onAnimalsChange}
              TriggerComponent={AssocAnimalFromNoteTrigger}
            />
          </div>
        )}

        {permissions?.parcels_module && parcels && parcels.length > 0 && (
          <RenderFunctionDrawer
            renderTrigger={
              <div
                className={
                  "flex h-[45px] w-[60px] items-center justify-center gap-1.5 text-amber-400"
                }
              >
                {draftNote.parcels?.length > 0 && (
                  <div className={"pr-1 text-body-xs"}>
                    {draftNote.parcels.length}
                  </div>
                )}
                <FieldIcon className={"h-[27px] w-[27px] text-amber-400"} />
              </div>
            }
            multiple
            title={"selectParcels"}
            options={parcelOptions}
            selectedValues={draftNote.parcels}
            onSelectionChange={onParcelsChange}
            ItemComponent={AssociatedParcelCard}
          />
        )}

        {permissions?.parcels_module &&
          cropPlantings &&
          cropPlantings.length > 0 && (
            <RenderFunctionDrawer
              renderTrigger={
                <div
                  className={
                    "flex h-[45px] w-[60px] items-center justify-center gap-1.5 text-amber-400"
                  }
                >
                  {draftNote.crop_plantings?.length > 0 && (
                    <div className={"pr-1 text-body-xs"}>
                      {draftNote.crop_plantings.length}
                    </div>
                  )}
                  <CropIcon className={"h-[27px] w-[27px] text-amber-400"} />
                </div>
              }
              multiple
              title={"selectCropPlantings"}
              options={cropPlantingsOptions}
              selectedValues={draftNote.parcels}
              onSelectionChange={onCropPlantingsChange}
              ItemComponent={AssociatedCropPlantingCard}
            />
          )}

        {permissions?.bee_module && apiaries && apiaries?.length > 0 && (
          <>
            <div
              className={"flex h-[45px] w-[60px] items-center justify-center"}
            >
              <RenderFunctionDrawer
                multiple
                ItemComponent={HiveCard}
                renderTrigger={
                  <div className={"flex gap-1 text-amber-400"}>
                    {draftNote.hives?.length > 0 && (
                      <div className={"pr-1 text-body-xs"}>
                        {draftNote.hives.length}
                      </div>
                    )}
                    <HiveIcon className={"scale-[1.75]"} />
                  </div>
                }
                title={"selectHives"}
                options={hiveOptions}
                selectedValues={draftNote.hives}
                onSelectionChange={onHivesChange}
              />
            </div>

            <div
              className={"flex h-[45px] w-[60px] items-center justify-center"}
            >
              <RenderFunctionDrawer
                multiple
                ItemComponent={ApiaryCard}
                renderTrigger={
                  <div className={"flex gap-1.5 text-amber-400"}>
                    {draftNote.apiaries?.length > 0 && (
                      <div className={"text-body-xs"}>
                        {draftNote.apiaries.length}
                      </div>
                    )}
                    <ApiariesIcon className={"scale-125"} />
                  </div>
                }
                title={"selectApiaries"}
                options={apiariesOptions}
                selectedValues={draftNote.apiaries}
                onSelectionChange={onApiariesChange}
              />
            </div>

            <div
              className={"flex h-[45px] w-[60px] items-center justify-center"}
            >
              <RenderFunctionDrawer
                multiple
                ItemComponent={AssociatedQueenCard}
                renderTrigger={
                  <div className={"flex gap-1.5 text-amber-400"}>
                    {draftNote.queens?.length > 0 && (
                      <div className={"text-body-xs"}>
                        {draftNote.queens.length}
                      </div>
                    )}
                    <NrBeesBeginningIcon className={"scale-150"} />
                  </div>
                }
                title={"selectQueens"}
                options={queenOptions}
                selectedValues={draftNote.queens}
                onSelectionChange={onQueensChange}
              />
            </div>
          </>
        )}

        {permissions?.bird_module && birdsGroups && birdsGroups.length > 0 && (
          <div className={"flex h-[45px] w-[60px] items-center justify-center"}>
            <RenderFunctionDrawer
              multiple
              ItemComponent={AssociatedBirdGroupCard}
              renderTrigger={
                <div className={"flex gap-1.5 text-amber-400"}>
                  {draftNote.birds_groups?.length > 0 && (
                    <div className={"text-body-xs"}>
                      {draftNote.birds_groups.length}
                    </div>
                  )}
                  <BirdIcon className={"scale-125"} />
                </div>
              }
              title={"selectBirdGroups"}
              options={birdsGroupsOptions}
              selectedValues={draftNote.birds_groups}
              onSelectionChange={onBirdsGroupsChange}
            />
          </div>
        )}
      </div>

      <FormatOverlay
        isFormatActive={isFormatActive}
        setIsFormatActive={setIsFormatActive}
        toggleFormat={toggleFormat}
        formatState={formatState}
        adjustIndentation={adjustIndentation}
      />
    </span>
  )
}

export default CustomToolbar
